import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaUpload, FaRobot, FaTags, FaRegCommentDots, FaArrowLeft, FaCheck } from "react-icons/fa";
import UploadPost from "./UploadPost";
import TypeOfAutomation from "./TypeOfAutomation";
import Keywords from "./Keywords";
import Message from "./Message";
import { createAutomation, getAutomationById, updateAutomation } from "../../services/automationService";
import AIAssistant from "../../../components/AI_Implementation/AIAssistant/AIAssistant";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { INFLUENCER_BASE } from "../../constants";

const InstagramAutomation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector((state) => state.user.data);
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingAutomation, setIsLoadingAutomation] = useState(false);
  const isEditMode = !!id;

  const [automationData, setAutomationData] = useState({
    uploadPost: null,
    typeOfAutomation: null,
    keywords: null,
    productLink: null,
    message: null,
    sendReplyToCommentFirst: false,
    replyCommentMessage: "",
    followerGate: {
      enabled: false,
      accessPromptMessage: "",
      accessButtonTitle: "",
      followPromptMessage: "",
      visitProfileButtonTitle: "",
      followingButtonTitle: "",
      profileUrl: "",
    },
  });

  useEffect(() => {
    if (id) {
      loadAutomationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAutomationData = async () => {
    if (!id) return;

    try {
      setIsLoadingAutomation(true);
      const response = await getAutomationById(id);

      if (response.success && response.data) {
        const automation = response.data;
        setAutomationData({
          uploadPost: automation.uploadPost || null,
          typeOfAutomation: automation.typeOfAutomation || null,
          keywords: automation.keywords || null,
          productLink: automation.productLink || null,
          message: automation.message || null,
          sendReplyToCommentFirst: automation.sendReplyToCommentFirst || false,
          replyCommentMessage: automation.replyCommentMessage || "Thank you! Check your DM for the link 😊",
          followerGate: {
            enabled: false,
            accessPromptMessage: "",
            accessButtonTitle: "",
            followPromptMessage: "",
            visitProfileButtonTitle: "",
            followingButtonTitle: "",
            profileUrl: "",
            ...(automation.followerGate || {}),
            enabled: !!automation.followerGate?.enabled,
          },
        });
      } else {
        toast.error("Failed to load automation data");
        navigate(`${INFLUENCER_BASE}/dashboard/automation-dashboard`);
      }
    } catch (error) {
      console.error("Error loading automation:", error);
      toast.error(error.error || error.message || "Failed to load automation");
      navigate(`${INFLUENCER_BASE}/dashboard/automation-dashboard`);
    } finally {
      setIsLoadingAutomation(false);
    }
  };

  const steps = [
    { id: 1, name: "Upload Post", icon: <FaUpload /> },
    { id: 2, name: "Type of Automation", icon: <FaRobot /> },
    { id: 3, name: "Keywords", icon: <FaTags /> },
    { id: 4, name: "Message", icon: <FaRegCommentDots /> },
  ];

  const getStepName = (stepId) => {
    switch (stepId) {
      case 1: return "Upload Post";
      case 2: return "Type of Automation";
      case 3: return "Keywords";
      case 4: return "Message";
      default: return "";
    }
  };

  // Handle step data from child components
  const handleNext = (stepData) => {
    // Merge step data into automationData
    setAutomationData((prevData) => ({
      ...prevData,
      ...stepData,
    }));

    // Move to next step
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handlePrevious = () => {
    setActiveStep((prevStep) => Math.max(1, prevStep - 1));
  };

  const handleStepClick = (stepId) => {
    // Allow navigation to completed steps only
    const currentStep = activeStep;
    if (stepId <= currentStep) {
      setActiveStep(stepId);
    }
  };

  // Submit automation data to backend
  const handleFinalSubmit = async (finalPayload = null) => {
    // Get instagramId from Redux state (only needed for create, not update)
    const instagramId = user?.instagramId || user?.social?.instagram?.instagramId || null;
    
    if (!isEditMode && !instagramId) {
      toast.error("Instagram ID not found. Please ensure your Instagram account is connected.");
      return;
    }

    // Normalize payload from Message step:
    // - Legacy flow: finalPayload is a string (message only)
    // - New flow: finalPayload is an object with message + reply settings
    let finalMessage = null;
    let finalSendReplyToCommentFirst = automationData.sendReplyToCommentFirst || false;
    let finalReplyCommentMessage = automationData.replyCommentMessage || "";
    let finalFollowerGate = automationData.followerGate || { enabled: false };

    if (typeof finalPayload === "string" || finalPayload === null) {
      finalMessage = finalPayload || automationData.message;
    } else if (typeof finalPayload === "object" && finalPayload !== null) {
      finalMessage = finalPayload.message || automationData.message;
      if (typeof finalPayload.sendReplyToCommentFirst === "boolean") {
        finalSendReplyToCommentFirst = finalPayload.sendReplyToCommentFirst;
      }
      if (typeof finalPayload.replyCommentMessage === "string") {
        finalReplyCommentMessage = finalPayload.replyCommentMessage;
      }
      if (finalPayload.followerGate) {
        finalFollowerGate = finalPayload.followerGate;
      }
    }

    // Auto-fill profile URL from connected Instagram username when gate is on.
    if (finalFollowerGate.enabled && !finalFollowerGate.profileUrl?.trim()) {
      const igUsername = user?.username || user?.social?.instagram?.username;
      if (igUsername) {
        finalFollowerGate = {
          ...finalFollowerGate,
          profileUrl: `https://www.instagram.com/${String(igUsername).replace(/^@/, "")}`,
        };
      }
    }

    const dataToSubmit = {
      uploadPost: automationData.uploadPost,
      typeOfAutomation: automationData.typeOfAutomation,
      keywords: automationData.keywords,
      productLink: automationData.productLink,
      message: finalMessage,
      sendReplyToCommentFirst: finalSendReplyToCommentFirst,
      replyCommentMessage: finalReplyCommentMessage,
      followerGate: finalFollowerGate,
      ...(isEditMode ? {} : { instagramId: instagramId }),
    };

    // Validate with the data we're about to submit
    const { uploadPost, typeOfAutomation, keywords, message } = dataToSubmit;

    if (!uploadPost || !uploadPost.title || !uploadPost.mediaLink) {
      toast.error("Please complete the Upload Post step");
      return;
    }

    if (!typeOfAutomation || !typeOfAutomation.type) {
      toast.error("Please select a type of automation");
      return;
    }

    if (!keywords || !Array.isArray(keywords) || keywords.length === 0) {
      toast.error("Please add at least one keyword");
      return;
    }

    if (!message || !message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      if (isEditMode) {
        response = await updateAutomation(id, dataToSubmit);
      } else {
        response = await createAutomation(dataToSubmit);
      }

      if (response.success) {
        toast.success(`Automation ${isEditMode ? 'updated' : 'created'} successfully!`);
        navigate(`${INFLUENCER_BASE}/dashboard/automation-dashboard`);
      }
    } catch (error) {
      const errorMessage = error.error || error.message || `Failed to ${isEditMode ? 'update' : 'create'} automation`;
      toast.error(errorMessage);
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} automation:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <UploadPost
            onNext={handleNext}
            data={automationData.uploadPost}
            handlePrevious={handlePrevious}
          />
        );
      case 2:
        return (
          <TypeOfAutomation
            onNext={handleNext}
            data={automationData.typeOfAutomation}
            handlePrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <Keywords
            onNext={handleNext}
            data={{ keywords: automationData.keywords, productLink: automationData.productLink }}
            handlePrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <Message
            onNext={handleNext}
            data={{
              message: automationData.message,
              productLink: automationData.productLink,
              sendReplyToCommentFirst: automationData.sendReplyToCommentFirst,
              replyCommentMessage: automationData.replyCommentMessage,
              followerGate: automationData.followerGate,
            }}
            handlePrevious={handlePrevious}
            onFinalSubmit={handleFinalSubmit}
            isSubmitting={isSubmitting}
            isEditMode={isEditMode}
          />
        );
      default:
        return null;
    }
  };

  if (isLoadingAutomation) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Loading automation..." />
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner
          message={isEditMode ? "Updating automation..." : "Creating automation..."}
        />
      </div>
    );
  }

  return (
    <div className="relative w-full p-4 lg:p-6">
      <button
        type="button"
        onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/automation-dashboard`)}
        className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600 lg:mb-4"
      >
        <FaArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
        <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">
          {isEditMode ? "Edit Automation" : "Make New Automation"}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditMode ? "Edit automation" : "Create new automation"}
          <span className="mx-1.5 text-gray-300">›</span>
          <span className="font-medium text-gray-700">{getStepName(activeStep)}</span>
        </p>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        {/* Step navigation */}
        <div className="w-full shrink-0 lg:w-64">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Steps
            </p>
            <div className="space-y-2">
              {steps.map((step) => {
                const isActive = activeStep === step.id;
                const isCompleted = step.id < activeStep;
                const isClickable = step.id <= activeStep;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => isClickable && handleStepClick(step.id)}
                    disabled={!isClickable}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                      isClickable ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                    } ${
                      isActive
                        ? "bg-orange-50 text-orange-600"
                        : isCompleted
                          ? "text-orange-600 hover:bg-orange-50/60"
                          : "text-gray-400"
                    }`}
                  >
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs transition ${
                        isActive || isCompleted
                          ? "bg-orange-500 text-white"
                          : "border border-gray-300 bg-white text-gray-400"
                      }`}
                    >
                      {isCompleted && !isActive ? <FaCheck className="h-3 w-3" /> : step.icon}
                    </div>
                    <span className="text-sm font-medium">{getStepName(step.id)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step content — child forms keep their own Next/Previous/Submit */}
        <div className="min-w-0 flex-1 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:p-6">
          {renderContent()}
        </div>
      </div>

      <AIAssistant context="automation" />
    </div>
  );
};

export default InstagramAutomation;
